// The write-up a scheduled pass left on a project, as the run page shows it, for an
// earlier briefing version's history.
import { useMemo } from "react";

import { useProjectAgentStatus } from "../api";
import { useStatefulRun } from "../api/hooks/useStatefulAgents";
import { useBriefingView } from "../utils/briefingView";
import { ProjectResponse } from "../components/ProjectResponse";
import { reviewAttrs, useReviewOnReply } from "../utils/reviewState";
import { useCitations } from "../components/useCitations";
import { useScrollToHash } from "../utils/useScrollToHash";
import styles from "./ProjectStatusTab.module.css";

export const LatestBriefing = ({ projectId }: { projectId: string }) => {
  const { version } = useBriefingView();
  const status = useProjectAgentStatus(projectId || null, version);
  const runId = status.data?.latest_run_id ?? null;
  const run = useStatefulRun(runId);
  const view =
    run.data?.projects?.find((p) => p.project_id === projectId) ?? null;
  const bodies = useMemo(
    () =>
      view
        ? [
            view.coordination,
            ...[...view.specialists, ...view.earlier].map((s) => s.reply),
          ]
        : [],
    [view],
  );
  const { components, transform, modal } = useCitations(bodies);
  const onReply = useReviewOnReply("briefing", "", null);
  useScrollToHash(!!view);

  if (status.isLoading || (runId && run.isLoading)) {
    return <p className={styles.muted}>Loading the briefing…</p>;
  }
  if (status.error || run.error) {
    return <p className={styles.error}>Could not load the briefing.</p>;
  }
  if (!runId) {
    return (
      <p className={styles.muted}>
        No scheduled pass has worked this project yet. The first one writes this
        briefing.
      </p>
    );
  }
  if (!view) {
    return (
      <p className={styles.muted}>
        The latest update left no briefing for this project.
      </p>
    );
  }
  return (
    <>
      <div className={styles.briefingCard} {...reviewAttrs("briefing", "")}>
        <ProjectResponse
          runId={runId}
          project={view}
          onReply={onReply}
          citations={{ components, transform }}
          runLinks={false}
        />
      </div>
      {modal}
    </>
  );
};
